import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useProjects } from "../lib/ProjectContext";
import { api, ApiError } from "../lib/apiClient";
import { postAskStream } from "../lib/askStream";
import { cacheThread, readCache, type CachedTurn } from "../lib/answerCache";
import { DirectiveAnswer } from "../components/DirectiveAnswer";
import { isClarificationCompleted } from "../../shared/clarificationState";
import { MAX_HISTORY_TURNS } from "@shared/conversation";
import type { IdentityUser } from "../lib/identity";
import type { AskResponse, ClarifyOption, ConversationTurn, StandardCategory, StandardCategoryOption } from "@shared/types";

interface Turn {
  question: string;
  answer: AskResponse;
  cached: boolean;
}

interface AskNavState {
  newChatAt?: number;
  restoreThread?: { threadId: string; turns: CachedTurn[] };
}

const COMPOSER_MAX_HEIGHT_PX = 200;

// Grows the textarea to fit its content up to a cap, then lets it scroll —
// resetting height to "auto" first is what makes it shrink back down too,
// not just grow (scrollHeight alone never reports a smaller value).
function autoGrow(el: HTMLTextAreaElement) {
  el.style.height = "auto";
  el.style.height = `${Math.min(el.scrollHeight, COMPOSER_MAX_HEIGHT_PX)}px`;
}

// Mirrors netlify/functions/_shared/validation.ts's askRequestSchema — a
// combined budget across every attachment on one question, not per-file,
// because the whole request has to fit under Netlify Functions' 6MB
// synchronous payload cap (see MAX_HTTP_UPLOAD_BYTES there for why). Kept as
// a local copy rather than a shared import since the two sides check it for
// different reasons (client-side UX vs. server-side enforcement).
const MAX_ATTACHMENT_BYTES = 4 * 1024 * 1024;
const MAX_ATTACHMENTS = 4;
const ACCEPTED_ATTACHMENT_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];
const ATTACHMENT_ACCEPT_ATTR = ".png,.jpg,.jpeg,.webp,.pdf,.docx," + ACCEPTED_ATTACHMENT_TYPES.join(",");

interface Attachment {
  id: string;
  base64: string;
  mimeType: string;
  filename: string;
  byteSize: number;
  // Only set for images — a PDF/DOCX chip shows a file icon instead.
  previewUrl: string | null;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(",")[1] ?? "");
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function AskPage({ user }: { user: IdentityUser }) {
  const { selectedProjectId, projects, loading: projectsLoading } = useProjects();
  const location = useLocation();
  const navigate = useNavigate();
  const [question, setQuestion] = useState("");
  const [turns, setTurns] = useState<Turn[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [attachError, setAttachError] = useState<string | null>(null);
  const [showSourceScope, setShowSourceScope] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<StandardCategory | "all">("all");
  const [categories, setCategories] = useState<StandardCategoryOption[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [lastCategory, setLastCategory] = useState<StandardCategory | "all" | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // The stable id for the current conversation — the first turn's auditId,
  // reused for every follow-up so cacheThread() updates one sidebar entry
  // instead of creating a new one per answer. Reset to null wherever turns
  // resets (new project, new topic, new chat); set explicitly when
  // restoring a past thread from the sidebar.
  const threadIdRef = useRef<string | null>(null);
  // Tracks whichever project is selected *right now*, independent of any
  // closure a still-in-flight submitQuestion/submitWithClarifyOption call
  // captured when it started — lets those callbacks tell a stale response
  // (from a project the user has since switched away from) apart from a
  // current one before touching shared turn-list state.
  const activeProjectIdRef = useRef(selectedProjectId);

  // A thread is scoped to one project's document set — switching projects
  // mid-conversation would otherwise carry retrieval context toward
  // documents the newly selected project may not even have.
  useEffect(() => {
    activeProjectIdRef.current = selectedProjectId;
    setTurns([]);
    setError(null);
    setShowSourceScope(false);
    setSelectedCategory("all");
    setLastCategory(null);
    threadIdRef.current = null;
  }, [selectedProjectId]);

  // Which standard categories are available to pick from for this project
  // (only ones with an approved document — see projects-standard-categories.ts).
  useEffect(() => {
    if (!selectedProjectId) return;
    setCategoriesLoading(true);
    api
      .get<{ categories: StandardCategoryOption[] }>(`/api/projects/${selectedProjectId}/standard-categories`)
      .then((data) => setCategories(data.categories))
      .catch(() => setCategories([]))
      .finally(() => setCategoriesLoading(false));
  }, [selectedProjectId]);

  // The sidebar's "New chat" and recent-chats list both navigate here with
  // location.state rather than owning this page's conversation state
  // directly — clear it once consumed so a refresh doesn't replay it.
  useEffect(() => {
    const state = location.state as AskNavState | null;
    if (!state) return;
    if (state.restoreThread) {
      threadIdRef.current = state.restoreThread.threadId;
      // Reopening a past conversation from Recent Chats is normal browsing,
      // not the true offline fallback `cached` is meant to flag (see the
      // genuine offline branch below) — marking it cached wrongly showed
      // "before you lost connection" on every reopened chat and hid the
      // feedback/correction UI for it (DirectiveAnswer.tsx gates both on
      // `!cached`), even though the underlying audit record is real and
      // feedback against it is perfectly valid.
      setTurns(state.restoreThread.turns.map((t) => ({ question: t.question, answer: t.response, cached: false })));
      setError(null);
    } else if (state.newChatAt) {
      threadIdRef.current = null;
      setTurns([]);
      setQuestion("");
      setError(null);
    }
    navigate(location.pathname, { replace: true, state: null });
    // Deliberately keyed on location.state alone — navigate()/location.pathname
    // are stable and re-including them here would just re-fire this on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // The compose box sits below the growing list of turns, so each new
  // answer pushes it further down the page — keep it in view instead of
  // making the inspector hunt for where to type the next question.
  useEffect(() => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [turns.length]);

  // Keyed on the value itself (not a change-event handler) so it covers
  // both typing and the programmatic reset to "" after a successful submit
  // — a reset needs the box to shrink back down too, not just grow.
  useEffect(() => {
    if (textareaRef.current) autoGrow(textareaRef.current);
  }, [question]);

  // Enter sends (matches Claude's own chat UI), Shift+Enter inserts a
  // newline. requestSubmit() re-runs the same validation/guards as the
  // button click (required field, no-op while a request is in flight)
  // instead of duplicating them here.
  function handleTextareaKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key !== "Enter" || e.shiftKey) return;
    e.preventDefault();
    formRef.current?.requestSubmit();
  }

  function removeAttachment(id: string) {
    setAttachments((prev) => {
      const target = prev.find((a) => a.id === id);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((a) => a.id !== id);
    });
    setAttachError(null);
  }

  function clearAttachments() {
    setAttachments((prev) => {
      for (const a of prev) if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
      return [];
    });
    setAttachError(null);
  }

  // Shared by the paperclip file picker and image paste — both just gather
  // a list of File objects and hand them here. Validated against the same
  // count/type/combined-size budget the server enforces (see
  // askRequestSchema), so a rejected attachment fails fast client-side
  // instead of round-tripping to the API first.
  async function addFiles(fileList: FileList | File[]) {
    const files = Array.from(fileList);
    if (files.length === 0) return;
    setAttachError(null);

    const unsupported = files.find((f) => !ACCEPTED_ATTACHMENT_TYPES.includes(f.type));
    if (unsupported) {
      setAttachError(`${unsupported.name || "That file"} isn't a supported type — attach an image, PDF, or Word document.`);
      return;
    }
    if (attachments.length + files.length > MAX_ATTACHMENTS) {
      setAttachError(`Only ${MAX_ATTACHMENTS} attachments are supported per question.`);
      return;
    }
    const existingBytes = attachments.reduce((sum, a) => sum + a.byteSize, 0);
    const newBytes = files.reduce((sum, f) => sum + f.size, 0);
    if (existingBytes + newBytes > MAX_ATTACHMENT_BYTES) {
      setAttachError(`Attachments total more than ${MAX_ATTACHMENT_BYTES / 1024 / 1024}MB combined — try fewer or smaller files.`);
      return;
    }

    const converted = await Promise.all(
      files.map(async (file) => ({
        id: crypto.randomUUID(),
        base64: await fileToBase64(file),
        mimeType: file.type,
        filename: file.name,
        byteSize: file.size,
        previewUrl: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
      })),
    );
    setAttachments((prev) => [...prev, ...converted]);
  }

  function handleAttachClick() {
    fileInputRef.current?.click();
  }

  function handleFileInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files.length > 0) void addFiles(e.target.files);
    e.target.value = "";
  }

  async function handleTextareaPaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    const imageFiles = Array.from(e.clipboardData.items)
      .filter((i) => i.type.startsWith("image/"))
      .map((i) => i.getAsFile())
      .filter((f): f is File => Boolean(f));
    if (imageFiles.length === 0) return;
    e.preventDefault();
    await addFiles(imageFiles);
  }

  // Normal questions search all applicable sources automatically. The optional
  // Search scope control below lets an experienced user deliberately narrow
  // retrieval, but it never interrupts the default Ask flow.
  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedProjectId || (!question.trim() && attachments.length === 0)) return;
    void submitQuestion(question, attachments, selectedCategory);
  }

  async function submitQuestion(askedQuestion: string, askedAttachments: Attachment[], category: StandardCategory | "all") {
    if (!selectedProjectId) return;
    const requestProjectId = selectedProjectId;
    const requestTurns = turns;
    setSubmitting(true);
    setError(null);
    const history: ConversationTurn[] = requestTurns
      .slice(-MAX_HISTORY_TURNS)
      .map((t) => ({ question: t.question, directive: t.answer.directive }));
    try {
      const result = await postAskStream<AskResponse>(`/api/projects/${requestProjectId}/ask`, {
        question: askedQuestion,
        history,
        category,
        threadId: threadIdRef.current ?? undefined,
        ...(askedAttachments.length > 0
          ? { attachments: askedAttachments.map((a) => ({ base64: a.base64, mimeType: a.mimeType, filename: a.filename })) }
          : {}),
      });
      const threadId = threadIdRef.current ?? result.auditId;
      const updatedTurns = [...requestTurns, { question: askedQuestion, answer: result, cached: false }];
      cacheThread(
        user.id,
        requestProjectId,
        threadId,
        updatedTurns.map((t) => ({ question: t.question, response: t.answer })),
      );
      // The user may have switched to a different project while this request
      // was in flight — that project's own useEffect already reset turns/
      // threadIdRef for it, so a stale response landing here must not
      // overwrite that state with this (now-inactive) project's answer.
      if (activeProjectIdRef.current !== requestProjectId) return;
      threadIdRef.current = threadId;
      setTurns(updatedTurns);
      setLastCategory(category);
      setQuestion("");
      clearAttachments();
      } catch (err) {
      if (!navigator.onLine) {
        const cachedThread = readCache(user.id, requestProjectId)[0];
        const cachedTurn = cachedThread?.turns[cachedThread.turns.length - 1];
        if (cachedTurn) {
          if (activeProjectIdRef.current === requestProjectId) {
            setTurns((prev) => [...prev, { question: askedQuestion, answer: cachedTurn.response, cached: true }]);
            setQuestion("");
          }
                setSubmitting(false);
          return;
        }
      }
      if (activeProjectIdRef.current === requestProjectId) {
        setError(err instanceof ApiError ? err.message : "Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  // Re-asks the exact same question that got a "clarify" response back,
  // this time with the option the inspector picked — see DirectiveAnswer's
  // clarify branch. Appends a new turn rather than replacing the clarify
  // one, same append-only history every other follow-up already uses.
  // Exactly one of drainageKindHint/pipeTypeHint is set on any given
  // ClarifyOption (see shared/types.ts) — whichever it is gets forwarded.
  async function submitWithClarifyOption(originalQuestion: string, option: ClarifyOption) {
    if (!selectedProjectId) return;
    const requestProjectId = selectedProjectId;
    const requestTurns = turns;
    setSubmitting(true);
    setError(null);
    const history: ConversationTurn[] = requestTurns
      .slice(-MAX_HISTORY_TURNS)
      .map((t) => ({ question: t.question, directive: t.answer.directive }));
    try {
      const result = await postAskStream<AskResponse>(`/api/projects/${requestProjectId}/ask`, {
        question: originalQuestion,
        history,
        category: lastCategory ?? "all",
        ...(option.drainageKindHint ? { drainageKindHint: option.drainageKindHint } : {}),
        ...(option.pipeTypeHint ? { pipeTypeHint: option.pipeTypeHint } : {}),
        threadId: threadIdRef.current ?? undefined,
      });
      const threadId = threadIdRef.current ?? result.auditId;
      const updatedTurns = [...requestTurns, { question: originalQuestion, answer: result, cached: false }];
      cacheThread(
        user.id,
        requestProjectId,
        threadId,
        updatedTurns.map((t) => ({ question: t.question, response: t.answer })),
      );
      if (activeProjectIdRef.current !== requestProjectId) return;
      threadIdRef.current = threadId;
      setTurns(updatedTurns);
    } catch (err) {
      if (activeProjectIdRef.current === requestProjectId) {
        setError(err instanceof ApiError ? err.message : "Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  function startNewTopic() {
    threadIdRef.current = null;
    setTurns([]);
    setQuestion("");
    setError(null);
    setShowSourceScope(false);
    setSelectedCategory("all");
    clearAttachments();
  }

  if (projectsLoading) return <p className="muted">Loading…</p>;
  if (projects.length === 0) {
    return (
      <div className="card">
        <p>No projects yet. Ask an owner to create one, or (if you're the owner) use the Sources screen to get started.</p>
      </div>
    );
  }

  return (
    <div className="content-col">
      {selectedProjectId &&
        turns.map((turn, turnIndex) => (
          <DirectiveAnswer
            key={turn.answer.auditId}
            question={turn.question}
            answer={turn.answer}
            cached={turn.cached}
            projectId={selectedProjectId}
            onSelectClarifyOption={(option) => submitWithClarifyOption(turn.question, option)}
            clarifySubmitting={submitting}
            clarifyCompleted={isClarificationCompleted(turnIndex, turns.length)}
            onSubmitClarifyText={(value) => void submitWithClarifyOption(turn.question, { label: value, pipeTypeHint: value })}
          />
        ))}

      <form className="composer" onSubmit={handleSubmit} ref={formRef}>
        <label className="composer-label" htmlFor="question">
          {turns.length > 0 ? "Ask a follow-up" : "Ask a field question"}
        </label>
        {attachments.length > 0 && (
          <div className="composer-attachments">
            {attachments.map((a) =>
              a.previewUrl ? (
                <div key={a.id} className="composer-attachment-chip">
                  <img src={a.previewUrl} alt={a.filename || "Attached image"} />
                  <button
                    type="button"
                    className="composer-attachment-chip-remove"
                    onClick={() => removeAttachment(a.id)}
                    aria-label={`Remove ${a.filename || "attached image"}`}
                  >
                    ×
                  </button>
                </div>
              ) : (
                <div key={a.id} className="composer-attachment-chip composer-attachment-chip-doc">
                  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                    <path
                      d="M6 2h9l5 5v15a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinejoin="round"
                    />
                    <path d="M14 2v5h5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
                  </svg>
                  <span className="composer-attachment-chip-name" title={a.filename}>
                    {a.filename}
                  </span>
                  <button
                    type="button"
                    className="composer-attachment-chip-remove"
                    onClick={() => removeAttachment(a.id)}
                    aria-label={`Remove ${a.filename || "attached file"}`}
                  >
                    ×
                  </button>
                </div>
              ),
            )}
          </div>
        )}
        <div className="composer-scope-row" style={{ display: "flex", gap: "0.6rem", alignItems: "center", flexWrap: "wrap", marginBottom: "0.65rem" }}>
          <button
            type="button"
            className="composer-new-topic"
            onClick={() => setShowSourceScope((open) => !open)}
            aria-expanded={showSourceScope}
          >
            {showSourceScope ? "Hide search scope" : "Change sources / Search scope"}
          </button>
          <span className="muted" style={{ fontSize: "0.78rem" }}>
            {selectedCategory === "all" ? "Automatically searching all applicable authoritative sources" : `Starting with ${categories.find((c) => c.category === selectedCategory)?.label ?? selectedCategory}; governing sources are also checked`}
          </span>
        </div>
        {showSourceScope && (
          <div className="category-picker" style={{ marginBottom: "0.75rem" }}>
            <p className="composer-label" style={{ marginBottom: "0.25rem" }}>Search scope</p>
            <p className="muted" style={{ marginBottom: "0.6rem" }}>Choose the starting source category. Governing specifications, directives, drawings, and cited references are checked across categories.</p>
            <div className="category-pills">
              <button
                type="button"
                className={selectedCategory === "all" ? "category-pill category-pill-all active" : "category-pill category-pill-all"}
                onClick={() => setSelectedCategory("all")}
              >
                All applicable sources
              </button>
              {!categoriesLoading && categories.map((c) => (
                <button
                  key={c.category}
                  type="button"
                  className={selectedCategory === c.category ? "category-pill active" : "category-pill"}
                  onClick={() => setSelectedCategory(c.category)}
                >
                  {c.label} <span className="category-pill-count">{c.documentCount}</span>
                </button>
              ))}
            </div>
            {categoriesLoading && <p className="muted" style={{ marginTop: "0.5rem" }}>Loading available source categories…</p>}
          </div>
        )}
        <div className="composer-bar">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ATTACHMENT_ACCEPT_ATTR}
            onChange={handleFileInputChange}
            style={{ display: "none" }}
          />
          <button
            type="button"
            className="composer-attach"
            onClick={handleAttachClick}
            disabled={attachments.length >= MAX_ATTACHMENTS}
            aria-label="Attach a photo, PDF, or Word document"
            title="Attach a photo, PDF, or Word document"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path
                d="M17.5 8.5 9.6 16.4a3 3 0 1 1-4.2-4.2l8.5-8.5a5 5 0 1 1 7.1 7.1L12.4 19.4a7 7 0 1 1-9.9-9.9L11 1"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <textarea
            id="question"
            ref={textareaRef}
            rows={1}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={handleTextareaKeyDown}
            onPaste={handleTextareaPaste}
            placeholder="e.g. What's the required cover depth for a 12&quot; RCP under a shoulder per the current standard drawings? (You can also attach a photo, PDF, or Word doc.)"
            required={attachments.length === 0}
          />
          <button
            className="composer-send"
            type="submit"
            disabled={submitting || (!question.trim() && attachments.length === 0)}
            aria-label={submitting ? "Checking sources…" : turns.length > 0 ? "Ask follow-up" : "Get directive"}
            title={submitting ? "Checking sources…" : "Send (Enter)"}
          >
            {submitting ? (
              <span className="composer-send-spinner" aria-hidden="true" />
            ) : (
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                <polyline points="9 10 4 15 9 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M20 4v7a4 4 0 0 1-4 4H4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </button>
        </div>
        <div className="composer-footer">
          <p className="composer-hint">Press Enter to send — CEI Nexus searches applicable authoritative sources automatically</p>
          {turns.length > 0 && (
            <button className="composer-new-topic" type="button" onClick={startNewTopic}>
              Start new topic
            </button>
          )}
        </div>
        {attachError && <p className="error-text" style={{ marginTop: "0.5rem" }}>{attachError}</p>}
        {error && <p className="error-text" style={{ marginTop: "0.5rem" }}>{error}</p>}
      </form>
    </div>
  );
}
